import { isWorklistTabWithComponentProps, isWorklistTabWithViewDefns } from 'configuration-schemas/Worklist';
import type { SubheaderDefnProps } from 'configuration-schemas/confdefnComponentProps';
import type { WorklistComponent, SectionViewConf } from 'configuration-schemas/confdefnComponents';
import { ConfDefnComponentType } from 'configuration-schemas/confdefnComponents';
import { WorklistViewDefn } from 'configuration-schemas/viewdefns/viewdefn';
import { get, isEmpty, isNil } from 'lodash';
import { AssortmentScopeSelectorSlice } from 'src/components/AssortmentScopeSelector/AssortmentScopeSelector.slice';
import { PendingRedecorateActionPayload } from 'src/components/ConfigurableGrid/ConfigurableGrid.types';
import type { SubheaderSlice } from 'src/components/Subheader/Subheader.slice';
import type { AppState } from 'src/store';

function isOfType<T extends Record<string, unknown>>(
  confType: ConfDefnComponentType,
  sectionView: T | undefined,
): sectionView is T {
  return !isNil(sectionView) && 'component' in sectionView && sectionView['component'] === confType;
}

/**
 * A partially dynamic helper method that removes the need for individual type predicates for each component above.
 * This is mainly used in epics to narrow down the types of the componentProps (a.k.a ownProps).
 *
 * e.g. isComponentType(ConfDefnComponentType.canvasView)
 *
 * @param {ConfDefnComponentType} componentType - the mapped name of the component type to verify.
 * @param {any} conf - the conf of the activePage or activeSubPage; the type is really a `SectionViewConf`
 *
 * @returns boolean
 */
export function isOfComponentType<T extends Record<string, unknown>>(
  type: ConfDefnComponentType,
  conf: any,
): conf is T {
  return isOfType<T>(type, conf);
}

// TODO: want to use AppState for arg below, currently supporting the legacy worklist tabs viewdefn structure
// (shape that has no `componentProps`) causes compilation issues inside the worklist conditional
export function maybeGetSubheaderComponentProps(state: any): SubheaderDefnProps | null {
  if (isNil(state.perspective)) {
    return null;
  }

  const activePageConf = state.perspective.activePageConf;
  const activeSubPage = state.perspective.activeSubPage;
  const worklistConf = state.worklist;

  // Check if activePageConf has subheader componentProps
  if (isOfComponentType<WorklistComponent>(ConfDefnComponentType.worklist, activePageConf)) {
    // Check if worklist has subheader componentProps
    if (activeSubPage && worklistConf && worklistConf.viewDefn) {
      const tabConf = worklistConf.viewDefn.tabs.find((tab: any) => {
        return tab.pathSlot === activeSubPage;
      });
      if (tabConf && tabConf.componentProps && tabConf.componentProps.defns && tabConf.componentProps.defns.subheader) {
        return tabConf.componentProps.defns.subheader;
      }
    }
  } else if (
    // this condition is second because the worklist could have a subheader defn
    activePageConf &&
    activePageConf.componentProps &&
    activePageConf.componentProps.defns &&
    activePageConf.componentProps.defns.subheader
  ) {
    return activePageConf.componentProps.defns.subheader;
  }

  return null;
}

// TODO: want to use AppState for arg below, currently supporting the legacy worklist tabs viewdefn structure
// (shape that has no `componentProps`) causes compilation issues inside the worklist conditional
export function maybeGetComponentProps<T extends SectionViewConf>(
  state: any,
  componentType: ConfDefnComponentType,
): T['componentProps'] | null {
  if (isNil(state.perspective)) {
    return null;
  }
  const activePageConf = state.perspective.activePageConf;
  const activeSubPage = state.perspective.activeSubPage;
  const worklistConf = state.worklist;

  if (isOfComponentType<T>(componentType, activePageConf)) {
    return activePageConf.componentProps;
  } else if (isOfComponentType<WorklistComponent>(ConfDefnComponentType.worklist, activePageConf)) {
    if (activeSubPage && worklistConf && worklistConf.viewDefn) {
      const tabConf = worklistConf.viewDefn.tabs.find((tab: any) => {
        return tab.pathSlot === activeSubPage;
      });
      if (tabConf && tabConf.componentType === componentType) {
        // insert top member, into tabs config
        if (isWorklistTabWithComponentProps(tabConf)) {
          const tabComponentProps = {
            ...tabConf.componentProps,
            topMembers: worklistConf.selectedItemId,
          };
          return tabComponentProps;
        } else if (isWorklistTabWithViewDefns(tabConf)) {
          const tabComponentProps = {
            ...tabConf,
            topMembers: worklistConf.selectedItemId,
          };
          return tabComponentProps;
        } else {
          // eslint-disable-next-line no-console
          console.info('unknown conf type detected for componentType:', componentType);
        }
      }
    }
  }

  return null;
}

export function isWorklistActive(state: AppState): boolean {
  const activePageConf = state.perspective.activePageConf;
  return isOfComponentType<WorklistComponent>(ConfDefnComponentType.worklist, activePageConf);
}

/**
 * This only detects correctly when navigating from worklist to non worklist of the same component type
 *
 * i.e Worklist -> Summary View Tab to Summary View
 */
export function isSameComponentType<T extends SectionViewConf>(
  state: AppState,
  componentType: ConfDefnComponentType,
): boolean {
  const activePageConf = state.perspective.activePageConf;
  const activeSubPage = state.perspective.activeSubPage;

  const isActivePage = isOfComponentType<T>(componentType, activePageConf);
  const isActiveSubPage = activeSubPage === componentType;

  return isActivePage && isActiveSubPage;
}

export function inputIsNotNullOrUndefined<T>(input: null | undefined | T | Array<null | undefined | T>): input is T {
  if (Array.isArray(input)) {
    return input.every(inputIsNotNullOrUndefined);
  }
  return input !== null && input !== undefined;
}

const getCurrentHideCompanion = (state: AppState) =>
  get<AppState, string, boolean>(state, 'perspective.activePageConf.componentProps.hideCompanion', false);

/**
 * Currently, this just just checks for hideCompanion to determine if
 * top member is needed
 * @param state
 * @returns
 */
export function worklistNeedsTopMember(state: AppState) {
  const maybeHideCompanion = getCurrentHideCompanion(state);
  return isWorklistActive(state) && !maybeHideCompanion;
}

/** This function is used many places to check if we are ready to load a view that can be loaded in a worklist view as a subpage
 * It checks for multiple conditions:
 * 1. First, if we're in a worklist and have `hideCompanion: true`, then we don't care whether we have a selected item and can skip subsequent checks
 * 2. Next we check if (again kinda) if we're not in a worklist
 * 3. Then, if we're not hiding companion, we check if there is a selected item to be used
 */
export function topMemInWorklistSelected(state: AppState) {
  if (!worklistNeedsTopMember(state)) {
    return true;
  } else {
    // Otherwise, we need a selected item to be ready
    const activeBreadcrumbRestore = state.breadcrumbs.activeBreadcrumbRestore;
    const selectedItemId = state.worklist.selectedItemId;
    const maybeRestoredItemId = activeBreadcrumbRestore?.selectedCompanionId ?? selectedItemId;
    return !isEmpty(maybeRestoredItemId);
  }
}

/**
 * This returns queryParams. We need this as we store queryParams after anchor, which
 * is technically invalid url semantics.
 * @returns queryparams
 */
export function getUrlQueryParams() {
  const paramsStr = get(window.location.hash.split('?'), 1);
  if (paramsStr != null) {
    return new URLSearchParams(paramsStr);
  } else return null;
}

export function topMemInUrl() {
  const queryParams = getUrlQueryParams();
  if (queryParams != null) {
    return queryParams.get('topMember');
  }
  return queryParams;
}

export function isScopeDefined(scope: AssortmentScopeSelectorSlice) {
  return scope.scopeSelected;
}

export function isSubheaderLoaded(subheader: SubheaderSlice) {
  return !subheader.subheaderLoading;
}
/** Gets active component type, even if on a worklist tab */
export function getActiveComponentType(state: AppState): ConfDefnComponentType | null {
  const activePageConf = state.perspective.activePageConf;

  if (!activePageConf) {
    return null;
  }

  // Worklist: use the active tab's componentType
  // eslint-disable-next-line @typescript-eslint/no-unsafe-enum-comparison
  if (activePageConf.component === ConfDefnComponentType.worklist) {
    const activeSubPage = state.perspective.activeSubPage;
    const worklistViewDefn = state.worklist.viewDefn as WorklistViewDefn | undefined;

    if (!activeSubPage || !worklistViewDefn) {
      return null;
    }

    const tab = worklistViewDefn.tabs.find((t) => t.pathSlot === activeSubPage);
    if (!tab) {
      return null;
    }

    return tab.componentType as ConfDefnComponentType;
  }

  return activePageConf.component as ConfDefnComponentType;
}

/** Used to ensure a component doens't refresh itself when requesting redecorate of other components
 * ex if Editable Style Pane is open over `ConfigurableGrid`, and you edit `ConfigurableGrid`,
 * we use the same `requestExternalDataRefresh` and we don't want `ConfigurableGrid` to
 * refresh itself from that action
 */
export const isNotSameExternalDataRefreshRequest = (
  { payload }: { payload: PendingRedecorateActionPayload },
  checkingComponent: string,
) => checkingComponent !== payload.sourceComponent;
